import { useQuery } from '@apollo/client/react'
import { dedupe } from '@/helpers/dedupe'
import { Table as BaseTable, type TableProps } from '../table'
import { usePageInfo } from '@/hooks/usePageInfo'
import { Slimline as Error } from '@/components/error'
import { LoadingSkeleton } from '@/components/base/loading-skeleton'
import { graphql } from '@/gql'
import type { EquipmentFilters, EquipmentOrder } from '@/gql/graphql'

const EquipmentQuery = graphql(`
  query Equipment($filters: EquipmentFilters, $order: EquipmentOrder, $after: String) {
    equipment(filters: $filters, order: $order, after: $after) {
      nodes {
        id
        name
        barcode
        equipmentType { id name equipmentCategory { id name } }
      }
      count
      pageInfo { hasNextPage hasPreviousPage startCursor endCursor }
    }
  }
`)

interface Props extends TableProps {
  filters?: EquipmentFilters
}

export const Table = ({ filters = {}, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery(EquipmentQuery, { variables: { filters } })

  const { pageInfo } = usePageInfo(data?.equipment?.pageInfo)

  if (loading) return <LoadingSkeleton table />

  return (
    <>
      { error && <Error error={ error } /> }
      { data && <BaseTable<EquipmentFilters, EquipmentOrder>
        { ...props }
        initialFilters={ filters }
        moreToFetch={ pageInfo.current?.hasNextPage }
        refetch={ refetch }
        totalRows={ data.equipment.count }
        fetchMore={ () => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.equipment.nodes = dedupe([...previousData.equipment.nodes, ...fetchMoreResult.equipment.nodes])
            return { ...fetchMoreResult }
          }
        }) }
        filters={ [
          {
            index: 0,
            type: 'text',
            placeholder: 'Start typing...',
            filterKey: 'nameCont',
          },
          {
            index: 1,
            type: 'text',
            placeholder: 'Start typing...',
            filterKey: 'barcodeCont',
          },
        ] }
        headings={ [
          { name: 'Name', sortField: 'name', defaultDirection: 'asc' },
          { name: 'Barcode', sortField: 'barcode', defaultDirection: 'asc' },
          { name: 'Type' },
          { name: 'Category' },
        ] }
        rows={ data?.equipment?.nodes.map(equipment => ({
          id: equipment.id,
          columns: [
            equipment.name,
            equipment.barcode,
            equipment.equipmentType?.name,
            equipment.equipmentType?.equipmentCategory?.name,
          ],
          link: `/equipment/${equipment.id}`,
        })) }
      /> }
    </>
  )
}
